import { isUnpublishedAdStudioTemplate } from "src/pages/ad-studio/components/is-unpublished-ad-studio-template";
import { useCallback, useMemo } from "react";

import {
  type AdStudioTemplatesQuery,
  useAdStudioTemplatesQuery,
} from "src/graphql";
import { selectAdStudioTemplateOptions } from "src/pages/ad-studio/components/ad-studio-template-selection";
import {
  type AdStudioTemplateCard,
  type AdStudioTemplateDraft,
  isPublishedAdStudioTemplate,
} from "src/pages/ad-studio/templates/types";

type AdStudioTemplateOptions = {
  /** False when no brand profile is chosen. */
  isEnabled: boolean;
  /** Undefined until the query resolves. */
  templates: AdStudioTemplateCard[] | undefined;
  drafts: (AdStudioTemplateDraft | AdStudioTemplateCard)[] | undefined;
};

/**
 * Active Ads templates assigned to the brand profile, the shared source for
 * the template picker and the selected-template pills in the composer.
 */
export function useAdStudioTemplateOptions(
  brandProfileId: string | undefined,
): AdStudioTemplateOptions {
  const isEnabled = Boolean(brandProfileId);
  const selectOptions = useCallback(
    (data: AdStudioTemplatesQuery) =>
      selectAdStudioTemplateOptions(
        data.getCurrentAgenticSettingsEntities,
        brandProfileId ?? "",
      ),
    [brandProfileId],
  );
  const { data: templates } = useAdStudioTemplatesQuery(
    { includeDraftTemplates: true },
    {
      enabled: isEnabled,
      select: selectOptions,
    },
  );

  return useMemo(
    () => ({
      isEnabled,
      templates: templates?.filter(isPublishedAdStudioTemplate),
      drafts: templates?.filter(isUnpublishedAdStudioTemplate),
    }),
    [isEnabled, templates],
  );
}
